import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import DashboardNavigation from '../../components/ui/DashboardNavigation';

import QuickActionPanel from '../../components/ui/QuickActionPanel';
import QuickStatsOverview from './components/QuickStatsOverview';
import DailyScheduleCard from './components/DailyScheduleCard';
import ClientManagementPanel from './components/ClientManagementPanel';
import CommissionTracker from './components/CommissionTracker';
import WorkoutProgramBuilder from './components/WorkoutProgramBuilder';
import ClientProgressMonitor from './components/ClientProgressMonitor';

const TrainerDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const { user } = useAuth();

  const mockStats = {
    activeClients: 24,
    clientsChange: 8,
    newClientsThisMonth: 3,
    sessionsToday: 6,
    completedSessions: 2,
    upcomingSessions: 4,
    monthlyEarnings: 8450,
    earningsChange: 12,
    weeklyEarnings: 2100,
    rating: 4.9,
    totalReviews: 87
  };

  const mockSchedule = [
  {
    id: 1,
    title: 'Personal Training - Mike Johnson',
    type: 'personal',
    time: '5:00 PM - 6:00 PM',
    client: 'Mike Johnson',
    location: 'Training Area 1',
    status: 'inProgress'
  },
  {
    id: 2,
    title: 'HIIT Class',
    type: 'class',
    time: '6:30 PM - 7:30 PM',
    location: 'Studio A',
    status: 'upcoming'
  },
  {
    id: 3,
    title: 'Personal Training - Sarah Williams',
    type: 'personal',
    time: '8:00 PM - 9:00 PM',
    client: 'Sarah Williams',
    location: 'Training Area 2',
    status: 'upcoming'
  },
  {
    id: 4,
    title: 'Available Slot',
    type: 'availability',
    time: '9:30 PM - 10:30 PM',
    location: 'Any Area',
    status: 'upcoming'
  }];


  const mockClients = [
  {
    id: 1,
    name: 'Mike Johnson',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_15f002d89-1763294705593.png",
    avatarAlt: 'Professional headshot of athletic man with short brown hair wearing blue athletic shirt',
    goal: 'Muscle Gain',
    nextSession: 'Today, 5:00 PM',
    progress: 78,
    isOnline: true
  },
  {
    id: 2,
    name: 'Sarah Williams',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_18d5ec201-1763297734654.png",
    avatarAlt: 'Professional headshot of fit woman with blonde ponytail wearing pink athletic top',
    goal: 'Weight Loss',
    nextSession: 'Today, 8:00 PM',
    progress: 65,
    isOnline: false
  },
  {
    id: 3,
    name: 'David Chen',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_10cadd384-1763292115349.png",
    avatarAlt: 'Professional headshot of Asian man with black hair wearing gray athletic shirt',
    goal: 'Strength Building',
    nextSession: 'Tomorrow, 6:00 AM',
    progress: 92,
    isOnline: true
  },
  {
    id: 4,
    name: 'Emily Rodriguez',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1d7334929-1763296602139.png",
    avatarAlt: 'Professional headshot of Hispanic woman with long dark hair wearing green athletic top',
    goal: 'Endurance',
    nextSession: 'Tomorrow, 7:00 PM',
    progress: 45,
    isOnline: false
  }];


  const mockEarnings = {
    total: 8450,
    personalTraining: 5200,
    personalSessions: 26,
    classInstruction: 2400,
    classCount: 18,
    programSales: 850,
    programsSold: 4,
    recentTransactions: [
    {
      id: 1,
      type: 'personal',
      description: 'Personal Training - Mike Johnson',
      date: 'Jan 25, 2026',
      amount: 80,
      status: 'Completed'
    },
    {
      id: 2,
      type: 'class',
      description: 'HIIT Class Instruction',
      date: 'Jan 24, 2026',
      amount: 120,
      status: 'Completed'
    },
    {
      id: 3,
      type: 'program',
      description: 'Advanced Strength Program Sale',
      date: 'Jan 23, 2026',
      amount: 250,
      status: 'Completed'
    }]

  };

  const mockProgressData = [
  {
    id: 1,
    clientName: 'Mike Johnson',
    clientAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_15f002d89-1763294705593.png",
    clientAvatarAlt: 'Professional headshot of athletic man with short brown hair wearing blue athletic shirt',
    achievement: 'Completed 8-week strength program',
    date: 'Today',
    metrics: [
    { label: 'Weight', value: '185 lbs', change: '+8 lbs', icon: 'Weight' },
    { label: 'Body Fat', value: '14%', change: '-3%', icon: 'Activity' },
    { label: 'Bench Press', value: '225 lbs', change: '+25 lbs', icon: 'Dumbbell' },
    { label: 'Squat', value: '315 lbs', change: '+35 lbs', icon: 'TrendingUp' }],

    photos: [
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_1d9390292-1764649132607.png",
      alt: 'Athletic man performing bench press exercise in gym with proper form',
      date: 'Week 1'
    },
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_166ca622d-1766484916931.png",
      alt: 'Athletic man doing squat exercise with barbell in modern gym facility',
      date: 'Week 4'
    },
    {
      url: "https://images.unsplash.com/photo-1735332181910-ad7fd4fe77f7",
      alt: 'Athletic man showing muscle definition progress in gym mirror selfie',
      date: 'Week 8'
    }]

  },
  {
    id: 2,
    clientName: 'Sarah Williams',
    clientAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_18d5ec201-1763297734654.png",
    clientAvatarAlt: 'Professional headshot of fit woman with blonde ponytail wearing pink athletic top',
    achievement: 'Lost 15 lbs in 6 weeks',
    date: 'Yesterday',
    metrics: [
    { label: 'Weight', value: '145 lbs', change: '-15 lbs', icon: 'Weight' },
    { label: 'Body Fat', value: '22%', change: '-5%', icon: 'Activity' },
    { label: 'Cardio Time', value: '45 min', change: '+15 min', icon: 'Clock' },
    { label: 'Calories', value: '1800', change: '-300', icon: 'Flame' }],

    photos: []
  }];


  const handleReschedule = (sessionId) => {
    console.log('Reschedule session:', sessionId);
  };

  const handleViewDetails = (sessionId) => {
    console.log('View session details:', sessionId);
  };

  const handleViewClient = (clientId) => {
    console.log('View client:', clientId);
  };

  const handleMessageClient = (clientId) => {
    console.log('Message client:', clientId);
  };

  const handleViewEarningsDetails = () => {
    console.log('View earnings details');
  };

  const handleCreateProgram = () => {
    console.log('Create custom program');
  };

  const handleUseAI = () => {
    console.log('Generate program with AI');
  };

  const handleViewFullProgress = () => {
    console.log('View full progress reports');
  };

  const handleQuickAction = (actionId) => {
    const actionMap = {
      'start-session': () => console.log('Start training session'),
      'view-schedule': () => navigate('/class-booking'),
      'client-notes': () => console.log('Open client notes')
    };

    if (actionMap?.[actionId]) {
      actionMap?.[actionId]();
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader />
      <div className="page-content-wrapper">
        <DashboardNavigation />
      </div>

      <main className="container mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
            Trainer Dashboard
          </h1>
          <p className="text-sm md:text-base text-muted-foreground">
            Manage your clients, schedule, and programs
          </p>
        </div>

        <div className="mb-6 md:mb-8">
          <QuickStatsOverview stats={mockStats} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
          <div className="lg:col-span-2">
            <DailyScheduleCard
              scheduleItems={mockSchedule}
              onReschedule={handleReschedule}
              onViewDetails={handleViewDetails} />

          </div>
          <div>
            <WorkoutProgramBuilder
              onCreateProgram={handleCreateProgram}
              onUseAI={handleUseAI} />

          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
          <ClientManagementPanel
            clients={mockClients}
            onViewClient={handleViewClient}
            onMessageClient={handleMessageClient} />

          <ClientProgressMonitor
            progressData={mockProgressData}
            onViewFullProgress={handleViewFullProgress} />

        </div>

        <div className="mb-6 md:mb-8">
          <CommissionTracker
            earnings={mockEarnings}
            onViewDetails={handleViewEarningsDetails} />

        </div>
      </main>

      <QuickActionPanel userRole={user?.role} onAction={handleQuickAction} />
    </div>);

};

export default TrainerDashboard;